// =============================================================================
// InviteDialog — ClickUp-style invitation dialog (wide, list-style)
// =============================================================================

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Loader2, Users, Clock, ChevronDown, Check, UserCircle, Shield, AlertTriangle, UserRoundMinus } from 'lucide-react';
import { ROLE_LABELS, type AppRole } from '@/modules/core/access-control/definitions';

type InviteErrorType = 'already_member' | 'pending_invitation' | 'generic' | null;

const INVITABLE_ROLES: { code: AppRole; label: string; description: string; icon: React.ReactNode }[] = [
  {
    code: 'member',
    label: ROLE_LABELS.member,
    description: 'Peut accéder à tous les éléments publics de votre workspace.',
    icon: <UserCircle className="h-5 w-5 text-muted-foreground" />,
  },
  {
    code: 'guest',
    label: ROLE_LABELS.guest,
    description: 'Ne peut pas utiliser toutes les fonctionnalités ni être ajouté aux espaces. Accès aux éléments partagés avec eux uniquement.',
    icon: <UserRoundMinus className="h-5 w-5 text-muted-foreground" />,
  },
  {
    code: 'admin',
    label: ROLE_LABELS.admin,
    description: 'Peut gérer les espaces, les personnes, la facturation et d\'autres paramètres du workspace.',
    icon: <Shield className="h-5 w-5 text-muted-foreground" />,
  },
];

interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvite: (email: string, role: string) => Promise<{ success: boolean; error?: string }>;
  onGoToMembers: () => void;
  /** If true, hides the role selector and forces 'member' role */
  forceRoleMember?: boolean;
}

export function InviteDialog({ open, onOpenChange, onInvite, onGoToMembers, forceRoleMember }: InviteDialogProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AppRole>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<{ type: InviteErrorType; email: string } | null>(null);
  const [rolePopoverOpen, setRolePopoverOpen] = useState(false);

  const selectedRole = INVITABLE_ROLES.find(r => r.code === role)!;
  const effectiveRole = forceRoleMember ? 'member' : role;

  const handleClose = (value: boolean) => {
    onOpenChange(value);
    if (!value) {
      setEmail('');
      setRole('member');
      setInviteError(null);
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    setInviteError(null);

    const result = await onInvite(email.trim(), effectiveRole);
    setInviting(false);

    if (result.success) {
      handleClose(false);
    } else {
      if (result.error?.includes('vous-même') || result.error?.includes('self_invite')) {
        setInviteError({ type: 'generic', email });
      } else if (result.error?.includes('déjà en attente')) {
        setInviteError({ type: 'pending_invitation', email });
      } else if (result.error?.includes('déjà membre')) {
        setInviteError({ type: 'already_member', email });
      } else {
        setInviteError({ type: 'generic', email });
      }
    }
  };

  const handleClearError = () => {
    setInviteError(null);
    setEmail('');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[620px] p-0 gap-0 overflow-hidden">
        <VisuallyHidden>
          <DialogTitle>Inviter des personnes</DialogTitle>
          <DialogDescription>
            Invitez des collaborateurs par e-mail dans votre workspace.
          </DialogDescription>
        </VisuallyHidden>
        {/* Body */}
        <div className="px-8 pt-8 pb-6">
          {/* Title — ClickUp style: large, with bold keyword */}
          <h2 className="text-xl font-normal text-foreground mb-6">
            Invitez des personnes <span className="font-bold">gratuitement</span>
          </h2>

          {/* Error states */}
          {inviteError?.type === 'already_member' && (
            <div className="flex items-start gap-3 p-3.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 mb-5">
              <Users className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm space-y-2">
                <p className="text-amber-800 dark:text-amber-200">
                  <strong>{inviteError.email}</strong> est déjà membre de ce workspace.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => { handleClose(false); onGoToMembers(); }} className="text-sm text-amber-700 dark:text-amber-300 underline underline-offset-2 hover:no-underline">
                    Voir les membres
                  </button>
                  <button onClick={handleClearError} className="text-sm text-amber-700 dark:text-amber-300 underline underline-offset-2 hover:no-underline">
                    Inviter quelqu'un d'autre
                  </button>
                </div>
              </div>
            </div>
          )}

          {inviteError?.type === 'pending_invitation' && (
            <div className="flex items-start gap-3 p-3.5 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/40 mb-5">
              <Clock className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm space-y-2">
                <p className="text-blue-800 dark:text-blue-200">
                  Une invitation est déjà en attente pour <strong>{inviteError.email}</strong>.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => handleClose(false)} className="text-sm text-blue-700 dark:text-blue-300 underline underline-offset-2 hover:no-underline">
                    Renvoyer depuis la liste
                  </button>
                  <button onClick={handleClearError} className="text-sm text-blue-700 dark:text-blue-300 underline underline-offset-2 hover:no-underline">
                    Inviter quelqu'un d'autre
                  </button>
                </div>
              </div>
            </div>
          )}

          {inviteError?.type === 'generic' && (
            <div className="flex items-start gap-3 p-3.5 rounded-lg bg-destructive/10 border border-destructive/30 mb-5">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <div className="text-sm space-y-2">
                <p className="text-destructive">Impossible d'envoyer l'invitation.</p>
                <button onClick={handleClearError} className="text-sm text-destructive underline underline-offset-2 hover:no-underline">
                  Réessayer
                </button>
              </div>
            </div>
          )}

          {/* Form */}
          {!inviteError && (
            <div className="space-y-6">
              {/* Email input — ClickUp list style */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Inviter par e-mail</label>
                <Input
                  type="email"
                  placeholder="E-mails, séparés par des virgules ou des espaces"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                  className="h-11"
                  autoFocus
                />
              </div>

              {/* Role selector — ClickUp list-row style (hidden if forceRoleMember) */}
              {!forceRoleMember && (
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Inviter comme</label>
                  <Popover open={rolePopoverOpen} onOpenChange={setRolePopoverOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-3 w-full text-left rounded-lg border border-border p-3.5 transition-colors hover:bg-[hsl(220,13%,91%)] dark:hover:bg-[hsl(220,13%,20%)]"
                      >
                        <span className="text-muted-foreground">{selectedRole.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-sm text-foreground">{selectedRole.label}</span>
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{selectedRole.description}</p>
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-1" align="start">
                      {INVITABLE_ROLES.map((r) => (
                        <button
                          key={r.code}
                          type="button"
                          onClick={() => { setRole(r.code); setRolePopoverOpen(false); }}
                          className="flex items-center gap-3 w-full text-left rounded-lg p-3 transition-colors hover:bg-[hsl(220,13%,91%)] dark:hover:bg-[hsl(220,13%,20%)]"
                        >
                          <span className="text-muted-foreground">{r.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm text-foreground">{r.label}</span>
                              {r.code === role && <Check className="h-4 w-4 text-primary shrink-0" />}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{r.description}</p>
                          </div>
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {forceRoleMember && (
                <p className="text-xs text-muted-foreground">
                  Les personnes invitées recevront le rôle <strong className="text-foreground">Membre</strong>.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer — ClickUp style: right-aligned, dark primary button */}
        {!inviteError && (
          <div className="flex items-center justify-end gap-3 px-8 py-4 border-t border-border">
            <Button
              variant="ghost"
              className="text-sm hover:bg-[hsl(220,13%,91%)] dark:hover:bg-[hsl(220,13%,20%)]"
              onClick={() => handleClose(false)}
            >
              Annuler
            </Button>
            <Button
              className="text-sm bg-foreground text-background hover:bg-foreground/90"
              onClick={handleInvite}
              disabled={inviting || !email.trim()}
            >
              {inviting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Envoyer une invitation gratuite
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
